import { NgClass } from '@angular/common';
import { Component, HostListener, Input, OnInit } from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { CardVariant, CardPadding } from '../models/card.model';

const cardVariants = cva({
  base: [
    'relative rounded-xl overflow-hidden',
    'transition-[transform,border-color,box-shadow,background-color] duration-300 ease-out',
    'backdrop-blur-md supports-[backdrop-filter]:backdrop-saturate-125',
    // Glass morphism base effects
    'before:content-[""] before:absolute before:inset-0',
    'before:pointer-events-none',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-60',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: [
        // Light theme
        'bg-white/84 border border-blue-200/45',
        'shadow-md shadow-slate-950/10',
        'hover:bg-white/90 hover:border-blue-200/65',
        'hover:shadow-lg hover:shadow-slate-950/14',
        // Dark theme
        'dark:bg-linear-to-br dark:from-slate-800/92 dark:via-slate-900/94 dark:to-blue-950/88 dark:border dark:border-blue-200/14',
        'dark:shadow-xl dark:shadow-slate-950/32',
        'dark:hover:from-slate-800/94 dark:hover:via-slate-900/96 dark:hover:to-blue-950/90 dark:hover:border-blue-200/20',
        'dark:hover:shadow-2xl dark:hover:shadow-slate-950/38',
      ].join(' '),
      elevated: [
        // Light theme
        'bg-white/86 border border-blue-200/50',
        'shadow-lg shadow-slate-950/12',
        'hover:bg-white/92 hover:border-blue-200/72',
        'hover:shadow-xl hover:shadow-slate-950/18',
        'hover:-translate-y-1',
        // Dark theme
        'dark:bg-linear-to-br dark:from-slate-800/94 dark:via-slate-900/95 dark:to-blue-950/90 dark:border dark:border-blue-200/16',
        'dark:shadow-xl dark:shadow-slate-950/36',
        'dark:hover:from-slate-800/96 dark:hover:via-slate-900/97 dark:hover:to-blue-950/92 dark:hover:border-blue-200/22',
        'dark:hover:shadow-2xl dark:hover:shadow-slate-950/42',
      ].join(' '),
      subtle: [
        // Light theme
        'bg-white/72 border border-blue-200/34',
        'shadow-sm shadow-slate-950/6',
        'hover:bg-white/80 hover:border-blue-200/52',
        // Dark theme
        'dark:bg-linear-to-br dark:from-slate-800/84 dark:via-slate-900/86 dark:to-blue-950/80 dark:border dark:border-blue-200/10',
        'dark:shadow-lg dark:shadow-slate-950/24',
        'dark:hover:from-slate-800/88 dark:hover:via-slate-900/90 dark:hover:to-blue-950/84 dark:hover:border-blue-200/16',
      ].join(' '),
      bordered: [
        // Light theme
        'bg-white/78 border-2 border-blue-300/42',
        'shadow-sm shadow-slate-950/8',
        'hover:border-blue-300/58',
        // Dark theme
        'dark:bg-linear-to-br dark:from-slate-800/88 dark:via-slate-900/92 dark:to-blue-950/86 dark:border-2 dark:border-blue-300/24',
        'dark:shadow-lg dark:shadow-slate-950/28',
        'dark:hover:border-blue-300/34',
      ].join(' '),
      product: [
        // Light theme
        'bg-white/82 border border-blue-200/42',
        'shadow-md shadow-slate-950/10',
        'hover:bg-white/90 hover:border-blue-200/62',
        'hover:shadow-lg hover:shadow-slate-950/16',
        'hover:-translate-y-2 hover:scale-[1.02]',
        'group cursor-pointer',
        // Dark theme
        'dark:bg-linear-to-br dark:from-slate-800/94 dark:via-slate-900/95 dark:to-blue-950/90 dark:border dark:border-blue-200/16',
        'dark:shadow-xl dark:shadow-slate-950/36',
        'dark:hover:from-slate-800/96 dark:hover:via-slate-900/97 dark:hover:to-blue-950/92 dark:hover:border-blue-200/24',
        'dark:hover:shadow-2xl dark:hover:shadow-slate-950/42',
      ].join(' '),
    },
    interactive: {
      true: 'cursor-pointer',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    interactive: 'false',
  },
});

type DeviceBreakpoints = {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
};

@Component({
  selector: 'ui-card-glass',
  standalone: true,
  imports: [NgClass],
  templateUrl: './card-glass.component.html',
  styleUrls: ['./card-glass.component.scss'],
})
export class CardGlassComponent implements OnInit {
  @Input() variant: CardVariant = 'default';
  @Input() padding: CardPadding = 'md';
  @Input() interactive: boolean = false;
  @Input() customClass: string = '';
  @Input() hasHeader: boolean = false;
  @Input() hasFooter: boolean = false;
  @Input() themeSection: ThemeSection = 'global';
  public devices: DeviceBreakpoints = { isMobile: false, isTablet: false, isDesktop: true };

  ngOnInit(): void {
    this.updateDevices();
  }

  public get computedClasses(): string {
    // Fallback for invalid variant/padding
    const validVariants = ['default', 'elevated', 'subtle', 'bordered', 'product'];
    const variant = validVariants.includes(this.variant) ? this.variant : 'default';
    const interactive = this.interactive ? 'true' : 'false';
    const readingShellOnMobile =
      typeof window !== 'undefined' &&
      window.innerWidth <= 768 &&
      this.customClass.includes('reading-shell');

    let classes =
      cardVariants({
        variant,
        interactive,
      }) +
      (readingShellOnMobile ? ' supports-[backdrop-filter]:!backdrop-saturate-100' : '') +
      (this.customClass ? ` ${this.customClass}` : '');

    if (readingShellOnMobile) {
      classes = classes
        .replace('backdrop-blur-md ', '')
        .replace('supports-[backdrop-filter]:backdrop-saturate-125 ', '');
    }

    return classes;
  }

  public get inlineBackdropFilter(): string | null {
    if (typeof window !== 'undefined' && window.innerWidth <= 768 && this.customClass.includes('reading-shell')) {
      return 'none';
    }

    return null;
  }

  public get shellPaddingClasses(): string {
    if (this.hasHeader || this.hasFooter) {
      return 'p-0';
    }
    return this.getResponsivePadding(this.padding, false);
  }

  public get contentClasses(): string {
    return this.hasHeader || this.hasFooter ? this.getResponsivePadding(this.padding, true) : '';
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.updateDevices();
  }

  private updateDevices(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const width = window.innerWidth;
    this.devices = {
      isMobile: width < 640,
      isTablet: width >= 640 && width < 1024,
      isDesktop: width >= 1024,
    };
  }

  private getResponsivePadding(padding: CardPadding, isSlottedContent: boolean): string {
    const normalizedPadding = ['none', 'sm', 'md', 'lg', 'xl'].includes(padding) ? padding : 'md';

    if (normalizedPadding === 'none') {
      return 'p-0';
    }

    if (normalizedPadding === 'sm') {
      return 'p-3';
    }

    if (normalizedPadding === 'md') {
      return this.devices.isDesktop && isSlottedContent ? 'p-6' : 'p-4';
    }

    if (normalizedPadding === 'lg') {
      if (this.devices.isMobile) {
        return 'p-4';
      }

      if (this.devices.isTablet) {
        return 'p-5';
      }

      return isSlottedContent ? 'p-8' : 'p-6';
    }

    if (this.devices.isMobile) {
      return 'p-4';
    }

    if (this.devices.isTablet) {
      return isSlottedContent ? 'p-6' : 'p-5';
    }

    return isSlottedContent ? 'p-10' : 'p-8';
  }
}

/**
 * Usage Examples:
 *
 * Simple card (no slots needed):
 * <ui-card-glass>
 *   <h3>Simple Card</h3>
 *   <p>Content goes here directly</p>
 * </ui-card-glass>
 *
 * Basic glassmorphism card with explicit content slot:
 * <ui-card-glass>
 *   <div slot="content">
 *     <h3>Glassmorphism Card</h3>
 *     <p>Beautiful frosted glass effect</p>
 *   </div>
 * </ui-card-glass>
 *
 * Product card with hover effects (simple):
 * <ui-card-glass variant="product" [interactive]="true">
 *   <img src="product.jpg" alt="Product" class="w-full h-48 object-cover rounded-lg mb-4">
 *   <h3 class="font-semibold text-lg">Amazing Product</h3>
 *   <p class="text-gray-600 dark:text-gray-300">Product description here...</p>
 * </ui-card-glass>
 *
 * Card with header, content, and footer (slots required):
 * <ui-card-glass variant="elevated" [hasHeader]="true" [hasFooter]="true">
 *   <div slot="header">
 *     <h3 class="text-lg font-semibold">Card Title</h3>
 *     <p class="text-sm opacity-75">Subtitle</p>
 *   </div>
 *
 *   <div slot="content" class="space-y-4">
 *     <p>Main content with glassmorphism effects</p>
 *   </div>
 *
 *   <div slot="footer" class="flex justify-between items-center">
 *     <span class="text-2xl font-bold">$99.99</span>
 *     <ui-button-glass size="sm">Add to Cart</button>
 *   </div>
 * </ui-card-glass>
 *
 * Theme-aware card (simple):
 * <ui-card-glass themeSection="header" variant="bordered">
 *   <p>Uses header theme colors</p>
 * </ui-card-glass>
 */
